import type { ChildControls } from "./parentTypes";

interface ChildControlsFormProps {
  controls: ChildControls;
  onChange: (controls: ChildControls) => void;
}

const TOGGLES: ReadonlyArray<{
  key: Exclude<keyof ChildControls, "studioToolLevel">;
  label: string;
  description: string;
}> = [
  {
    key: "allowNewDrawings",
    label: "Allow new drawings",
    description: "Children can create a new blank drawing.",
  },
  {
    key: "allowNewComics",
    label: "Allow new comics",
    description: "Children can create a new comic project.",
  },
  {
    key: "allowActivities",
    label: "Allow Drawing Activities",
    description: "Children can start guided drawings and creative challenges.",
  },
  {
    key: "allowProjectDeletion",
    label: "Allow children to delete projects",
    description: "Existing drawings can be permanently removed from the project library.",
  },
  {
    key: "allowBackupImport",
    label: "Allow backup import",
    description: "Children can import a local .perfectdraw backup.",
  },
  {
    key: "allowBackupExport",
    label: "Allow backup export",
    description: "Children can download a local project backup.",
  },
  {
    key: "allowDestructiveEditing",
    label: "Allow destructive editing actions",
    description:
      "Allows deleting selected art or dialogue, clearing panels, and clearing, deleting or merging layers.",
  },
];

export function ChildControlsForm({ controls, onChange }: ChildControlsFormProps) {
  const setToggle = (key: Exclude<keyof ChildControls, "studioToolLevel">, value: boolean): void =>
    onChange({ ...controls, [key]: value });

  return (
    <div className="parent-controls">
      <fieldset>
        <legend>Creation and project access</legend>
        {TOGGLES.slice(0, 6).map((item) => (
          <label className="parent-toggle" key={item.key}>
            <span>
              <strong>{item.label}</strong>
              <small>{item.description}</small>
            </span>
            <input
              checked={controls[item.key]}
              onChange={(event) => setToggle(item.key, event.target.checked)}
              type="checkbox"
            />
          </label>
        ))}
      </fieldset>

      <fieldset>
        <legend>Studio tool level</legend>
        <p>Existing artwork always remains visible, even when a tool is hidden.</p>
        <div className="parent-tool-levels">
          {(
            [
              ["basic", "Basic", "Freehand brushes, colours, size, opacity, zoom and history."],
              ["creative", "Creative", "Basic tools plus Fill, Pick Colour and shapes."],
              ["full", "Full", "Every tool, raster selection, Dialogue and Layers."],
            ] as const
          ).map(([value, label, description]) => (
            <label key={value}>
              <input
                checked={controls.studioToolLevel === value}
                name="studioToolLevel"
                onChange={() => onChange({ ...controls, studioToolLevel: value })}
                type="radio"
                value={value}
              />
              <span>
                <strong>{label}</strong>
                <small>{description}</small>
              </span>
            </label>
          ))}
        </div>
      </fieldset>

      <fieldset>
        <legend>Editing safety</legend>
        {TOGGLES.slice(6).map((item) => (
          <label className="parent-toggle" key={item.key}>
            <span>
              <strong>{item.label}</strong>
              <small>{item.description}</small>
            </span>
            <input
              checked={controls[item.key]}
              onChange={(event) => setToggle(item.key, event.target.checked)}
              type="checkbox"
            />
          </label>
        ))}
      </fieldset>
    </div>
  );
}
